import { create, xmlns, xlinkns } from "./svg";
import dpi from "../dpi";

class CircleGraph {
    constructor() {
        this.scale = dpi() / 25.4;
        this.attrScale = ["transform", `scale(${this.scale})`];
    }
    draw({
        angles_degr = [45, 90, 180],
        anglesLen_mm = [20, 20, 20],
        height_mm = 80,
        width_mm = 80,
        renderingType = "geometricPrecision", // auto | optimizeSpeed | crispEdges | geometricPrecision | inherit
        radParams: {
            Xcenter_mm = 40,
            Ycenter_mm = 40,
            radius_mm = 35,
            sectorsQty = 12,
            sectorNames = [
                "180°;-I",
                "-150°;+aVR",
                "-120°;-II",
                "-90°;-aVF",
                "-60°;-III",
                "-30°; +aVL",
                "0°;+I",
                "+30°;-aVR",
                "+60°;+II",
                "+90°;+aVF",
                "+120°;+III",
                "+150°;-aVL"
            ],
            shiftTextElectrAxisHeart_mm = 2,
            zoneAngles = [-105, -30, 0, 90, 120, 195],
            zoneNames = ["Резко влево", "Влево", "Нормальное положение ЭОС", "Вправо", "Резко вправо", "Резкое отклонение"],
            arrowLength_mm = 5,
            arrowTick_mm = 5,
            angles_color = ["#4286f4", "#d62427", "#1cd828"],
            angles_label = ["QRS", "P", "T"]
        } = {}
    } = {}) {
        const attrScale = this.attrScale,
            height_px = height_mm * this.scale,
            width_px = width_mm * this.scale;

        // SVG________________________
        const elSvg = create(
            "svg",
            ["xmlns", xmlns],
            ["xmlns:xlink", xlinkns],
            ["version", "1.1"],
            ["shape-rendering", renderingType],
            ["viewBox", `0 0 ${height_px} ${width_px}`]
        );

        // Circle_____________________
        const elCircle = create(
            "circle",
            attrScale,
            ["cx", Xcenter_mm],
            ["cy", Ycenter_mm],
            ["r", radius_mm],
            ["fill", "none"],
            ["stroke-width", "0.1"],
            ["style", "stroke:black;"]
        );
        elSvg.appendChild(elCircle);

        // Sectors____________________
        const gSectors = create("g", ["id", "SectorLines"], ["style", "stroke:#808080;stroke-width:0.25;stroke-dasharray:0.5;"], attrScale);

        // Sector names
        const gSectNames = create(
            "g",
            ["id", "sectorNames"],
            attrScale,
            ["dominant-baseline", "middle"],
            ["style", `font-family:Arial, sans-serif; font-size:${10 / this.scale}px; fill:black;`],
            ["text-anchor", "middle"]
        );
        for (let i = 0; i < sectorsQty; i++) {
            // position
            let alpha = i * (360 / sectorsQty) - 180;
            alpha = this._degrToRad(alpha);
            const { x, y } = this._polarToDec((radius_mm * 2.75) / 4, alpha);
            const { x: xstart, y: ystart } = this._polarToDec(1.5, alpha);

            const line = create(
                "line",
                ["x1", Xcenter_mm + xstart],
                ["y1", Ycenter_mm + ystart * -1],
                ["x2", Xcenter_mm + x],
                ["y2", Ycenter_mm + y * -1]
            );
            gSectors.appendChild(line);

            // name
            const { x: xtext, y: ytext } = this._polarToDec((radius_mm * 3.25) / 4, alpha);
            const names = sectorNames[i].split(";");

            const eltext = create("text", ["y", Ycenter_mm + ytext]);

            const elStr1 = create("tspan", ["dy", "-0.5em"], ["x", Xcenter_mm + xtext]);
            elStr1.textContent = names[0];
            eltext.appendChild(elStr1);

            const elStr2 = create("tspan", ["dy", "1em"], ["x", Xcenter_mm + xtext]);
            elStr2.textContent = names[1];
            eltext.appendChild(elStr2);

            gSectNames.appendChild(eltext);
        }
        elSvg.appendChild(gSectors);
        elSvg.appendChild(gSectNames);

        // Zones______________________
        // Out texts arcs baseline
        const gArrowsTicks = create("g", ["id", "ArrowsTicks"], attrScale);

        const gTicks = create("g", ["id", "Ticks"], ["style", "stroke:black;stroke-width:0.25"]);
        gArrowsTicks.appendChild(gTicks);

        const elDefs = create("defs");
        gArrowsTicks.appendChild(elDefs);

        const arcR = radius_mm + shiftTextElectrAxisHeart_mm,
            arAngleD_degr = (Math.asin(arrowLength_mm / (2 * radius_mm)) * 180) / Math.PI; // Вся стрелка должна лежать на окружности

        const sweepNeed = i => i >= 2 && i < 5;
        const N = zoneAngles.length;
        const fillArrow = [
            [true, false],
            [true, false],
            [true, true],
            [false, true],
            [false, true],
            [false, false]
        ];
        const zoneIds = Array(N)
            .fill("")
            .map((val, i) => `zone_${i}`);

        for (let i = 0; i < N; i++) {
            const ang1 = zoneAngles[i] * -1, // (*-1) Разное направление положительного угла медицинское и при прорисовке
                ang2 = zoneAngles[i < N - 1 ? i + 1 : 0] * -1;

            let { x: x1, y: y1 } = this._polarToDec(arcR, this._degrToRad(ang1));
            x1 += Xcenter_mm;
            y1 = y1 * -1 + Ycenter_mm; // *(-1) Инвертация оси ординат
            let { x: x2, y: y2 } = this._polarToDec(arcR, this._degrToRad(ang2));
            x2 += Xcenter_mm;
            y2 = y2 * -1 + Ycenter_mm;

            if (sweepNeed(i)) {
                let temp = x1;
                x1 = x2;
                x2 = temp;
                temp = y1;
                y1 = y2;
                y2 = temp;
            }
            elDefs.appendChild(this._textArc(zoneIds[i], arcR, x1, y1, x2, y2, !sweepNeed(i)));

            // Arrows and ticks
            gTicks.appendChild(this._tick(ang1, arrowTick_mm, radius_mm, Xcenter_mm, Ycenter_mm));
            let bFill1 = false,
                bFill2 = false;
            if (i < fillArrow.length) {
                bFill1 = fillArrow[i][0];
                bFill2 = fillArrow[i][1];
            }
            gArrowsTicks.appendChild(this._arrow(ang1, -arAngleD_degr, arrowLength_mm, bFill1, radius_mm, Xcenter_mm, Ycenter_mm));
            gArrowsTicks.appendChild(this._arrow(ang2, arAngleD_degr + 180, arrowLength_mm, bFill2, radius_mm, Xcenter_mm, Ycenter_mm));
        }
        elSvg.appendChild(gArrowsTicks);

        // Zone names
        const eltextGroup = create(
            "g",
            ["id", "ZoneNames"],
            attrScale,
            ["text-anchor", "middle"],
            ["style", `fill:black; stroke:none; stroke-width:0.1;font-family:Arial, sans-serif; font-size:${12 / this.scale}px;`],
            ["x", "0"],
            ["y", "0"]
        );

        for (let i = 0; i < N; i++) {
            const elText = create("text", sweepNeed(i) ? ["dominant-baseline", "hanging"] : null);
            const eltextPath = create("textPath", ["startOffset", "50%"], [xlinkns, "xlink:href", "#" + zoneIds[i]]);
            eltextPath.textContent = zoneNames[i];
            elText.appendChild(eltextPath);

            eltextGroup.appendChild(elText);
        }
        elSvg.appendChild(elDefs);
        elSvg.appendChild(eltextGroup);

        // Углы_____________________________
        const fonsize_px = 14;
        const gAngles = create("g", ["id", "Angles"], attrScale, ["style", "stroke-width:0.5"]);

        const gAngleNames = create(
            "g",
            ["id", "AngleNames"],
            attrScale,
            [
                "style",
                `font-family:Arial, sans-serif; font-size:${fonsize_px / this.scale}px; font-weight:900; stroke:white; stroke-width:0.2;`
            ],
            ["text-anchor", "middle"]
        );

        if (angles_degr !== null) {
            for (let i = 0; i < angles_degr.length; i++) {
                if (angles_degr[i] === undefined || angles_degr[i] === null) {
                    continue;
                }

                const ang = this._degrToRad(-angles_degr[i]);
                const { x, y } = this._polarToDec(anglesLen_mm[i], ang);
                const { x: xstart, y: ystart } = this._polarToDec(0.75, ang);

                const line = create(
                    "line",
                    ["x1", Xcenter_mm + xstart],
                    ["y1", Ycenter_mm + ystart * -1],
                    ["x2", Xcenter_mm + x],
                    ["y2", Ycenter_mm + y * -1],
                    ["style", `stroke:${angles_color[i]}`]
                );
                gAngles.appendChild(line);

                const { x: xtext, y: ytext } = this._polarToDec(anglesLen_mm[i] + 3, ang);
                const eltext = create(
                    "text",
                    ["style", `fill:${angles_color[i]}`],
                    ["y", Ycenter_mm + ytext * -1],
                    ["x", Xcenter_mm + xtext],
                    ["dy", `${fonsize_px / 2 / this.scale}`]
                );
                eltext.textContent = angles_label[i];
                gAngleNames.appendChild(eltext);
            }
        }
        elSvg.appendChild(gAngles);
        elSvg.appendChild(gAngleNames);
        return elSvg;
    }

    _polarToDec(_r, _alpha) {
        const x = _r * Math.cos(_alpha);
        const y = _r * Math.sin(_alpha);
        return {
            x,
            y
        };
    }
    _degrToRad(_degr) {
        // degr to [-360; 360]
        _degr = _degr % 360;
        // degr to [-180; +180]
        if (_degr < -180) _degr = 360 + _degr;
        else if (_degr > 180) _degr = -(360 - _degr);
        // degr to rad
        _degr = (_degr / 180) * Math.PI;
        return _degr;
    }
    _textArc(_id, _r, _x1, _y1, _x2, _y2, _sweep) {
        return create("path", ["id", _id], ["d", `M ${_x1},${_y1} A ${_r},${_r} 180, 0, ${_sweep ? 1 : 0} ${_x2},${_y2}`]);
    }
    _tick(_angle_deg, _tickLen, radius_mm, Xcenter_mm, Ycenter_mm) {
        const _angle = this._degrToRad(_angle_deg);

        const { x: x1, y: y1 } = this._polarToDec(radius_mm - _tickLen / 2, _angle);
        const { x: x2, y: y2 } = this._polarToDec(radius_mm + _tickLen / 2, _angle);
        const line = create(
            "line",
            ["x1", Xcenter_mm + x1],
            ["y1", Ycenter_mm + y1 * -1],
            ["x2", Xcenter_mm + x2],
            ["y2", Ycenter_mm + y2 * -1]
        );
        return line;
    }
    _arrow(_angle, _deltaAngle, arrowLen_mm, _bFill, radius_mm, Xcenter_mm, Ycenter_mm) {
        let { x, y } = this._polarToDec(radius_mm, this._degrToRad(_angle));
        x += Xcenter_mm;
        y = y * -1 + Ycenter_mm;
        const arrAngle1 = -1 * _angle - _deltaAngle + 90;

        const elArrow = create(
            "path",
            ["transform", `rotate(${arrAngle1} ${x} ${y}) `],
            ["d", `M ${x} ${y} l ${arrowLen_mm} ${1} q ${-1} ${-1} ${0} ${-2} Z`],
            ["fill", _bFill ? "black" : "white"],
            ["style", "stroke:black;"],
            ["stroke-width", "0.1"]
        );
        return elArrow;
    }
}

export default CircleGraph;
